import { useEffect, useState } from 'react';
import { api, getToken, setToken, Unauthorised, type Health } from './api';
import Login from './views/Login';
import Prices from './views/Prices';
import Slots from './views/Slots';
import Bookings from './views/Bookings';
import Coupons from './views/Coupons';

type Tab = 'bookings' | 'prices' | 'capacity' | 'coupons';

export default function App() {
  const [signedIn, setSignedIn] = useState(() => Boolean(getToken()));
  const [tab, setTab] = useState<Tab>('bookings');
  const [health, setHealth] = useState<Health | null>(null);
  const [drain, setDrain] = useState<string | null>(null);

  useEffect(() => {
    api.health().then(setHealth).catch(() => setHealth(null));
  }, []);

  // A 401 anywhere means the token expired; drop straight back to sign-in
  // rather than showing half a screen of errors.
  useEffect(() => {
    const onRejection = (e: PromiseRejectionEvent) => {
      if (e.reason instanceof Unauthorised) {
        setToken(null);
        setSignedIn(false);
      }
    };
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, []);

  if (!signedIn) return <Login onDone={() => setSignedIn(true)} />;

  return (
    <>
      <header className="top">
        <strong>Private Safari Dubai</strong>
        <nav>
          {(['bookings', 'prices', 'capacity', 'coupons'] as Tab[]).map((t) => (
            <button key={t} className="tab" aria-current={tab === t} onClick={() => setTab(t)}>
              {t[0]!.toUpperCase() + t.slice(1)}
            </button>
          ))}
        </nav>
        <div className="spacer" />
        {health && (
          <span className="small muted">
            {health.environment} · {health.availability_provider} · charges in {health.charge_currency}
          </span>
        )}
        <button
          className="link small"
          onClick={() => {
            void api
              .drainNotifications()
              .then((r) => setDrain(`Sent ${r.delivered}, failed ${r.failed}`))
              .catch((e) => setDrain(e instanceof Error ? e.message : String(e)));
          }}
          title="Re-send any queued confirmation emails and WhatsApp messages"
        >
          Resend queued messages
        </button>
        <button
          onClick={() => {
            setToken(null);
            setSignedIn(false);
          }}
        >
          Sign out
        </button>
      </header>

      <main>
        {/* Configuration problems that silently break things people rely on. */}
        {health && !health.stripe_configured && (
          <div className="warn">
            <strong>Stripe is not configured on the API.</strong> Booking attempts on the site will
            be refused with a “contact us” message. Nothing is being charged.
          </div>
        )}
        {health && !health.notifications_configured && (
          <div className="warn">
            <strong>No notification webhook is configured.</strong> Confirmations are being queued
            but not sent. They are not lost — use “Resend queued messages” once n8n is set up.
          </div>
        )}
        {health && health.availability_provider === 'own_fleet' && (
          <div className="warn small">
            Availability is running on the <strong>own-fleet</strong> model: capacity is vehicles ×
            time slots, and dispatch is driver assignment. This was an assumption, not a decision —
            see <code>docs/decisions.md</code> §1.
          </div>
        )}
        {drain && <p className="ok">{drain}</p>}

        {tab === 'bookings' && <Bookings />}
        {tab === 'prices' && <Prices deployHookConfigured={health?.deploy_hook_configured ?? false} />}
        {tab === 'capacity' && <Slots />}
        {tab === 'coupons' && <Coupons />}
      </main>
    </>
  );
}
